import { useMutation, useQuery } from '@apollo/client'
import { Button, Grid } from '@mui/material'
import ToggleEditMultiSelectField from 'candidate/components/ToggleEditFormFields/ToggleEditMultiSelectField'
import { GetCandidateSectorPreferenceOptions } from 'candidate/graphql/recruitingPreferences.gql'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useToast from 'shared/components/Toast/useToast'
import { UpdateCandidateSectorPreferences } from 'shared/graphql/candidatePreferences.gql'
import { DropdownOption } from 'shared/models/dropdownOption'
import { EditButtonContainer } from 'shared/style/styled'
import PreferenceSectionHeader from './PreferenceSectionHeader'
import { RecruitingFormComponentProps } from './preference.types'

const Sector: React.FC<RecruitingFormComponentProps> = ({
    candidate,
    handleEditArray,
    editArray,
    sectionName,
    setUnsavedChangesExist,
    refetch,
}) => {
    const [mutate] = useMutation(UpdateCandidateSectorPreferences)
    const toast = useToast()

    const isEdit = useMemo(
        () =>
            editArray.find((ele) => ele.sectionName === sectionName)?.isEdit ||
            false,
        [editArray, sectionName]
    )

    const sectorPreferences = useMemo(
        () => candidate.sectorPreferences ?? [],
        [candidate]
    )
    const SectorsOptions = useQuery(GetCandidateSectorPreferenceOptions)

    const [primarySectors, setPrimarySectors] = useState<DropdownOption[]>(
        sectorPreferences
            .filter(({ order }) => order === 1)
            .map((pref) => pref.preferenceSector)
    )
    const [secondarySectors, setSecondarySectors] = useState<DropdownOption[]>(
        sectorPreferences
            .filter(({ order }) => order === 2)
            .map((pref) => pref.preferenceSector)
    )
    const [tertiarySectors, setTertiarySectors] = useState<DropdownOption[]>(
        sectorPreferences
            .filter(({ order }) => order === 3)
            .map((pref) => pref.preferenceSector)
    )

    const resetForm = useCallback(() => {
        setPrimarySectors(
            sectorPreferences
                .filter(({ order }) => order === 1)
                .map((pref) => pref.preferenceSector)
        )
        setSecondarySectors(
            sectorPreferences
                .filter(({ order }) => order === 2)
                .map((pref) => pref.preferenceSector)
        )
        setTertiarySectors(
            sectorPreferences
                .filter(({ order }) => order === 3)
                .map((pref) => pref.preferenceSector)
        )
    }, [
        sectorPreferences,
        setPrimarySectors,
        setSecondarySectors,
        setTertiarySectors,
    ])

    // update formValues state if new data comes back from the API
    useEffect(() => {
        resetForm()
    }, [sectorPreferences, resetForm])

    // reset form when user cancels edit
    useEffect(() => {
        if (!isEdit) resetForm()
    }, [isEdit, resetForm])

    const unsavedChangesExist = useMemo(() => {
        if (!sectorPreferences) return false
        const primaryFromDb = sectorPreferences
            .filter(({ order }) => order === 1)
            .map((pref) => pref.preferenceSector.id)
        const secondaryFromDb = sectorPreferences
            .filter(({ order }) => order === 2)
            .map((pref) => pref.preferenceSector.id)
        const tertiaryFromDb = sectorPreferences
            .filter(({ order }) => order === 3)
            .map((pref) => pref.preferenceSector.id)
        if (
            primarySectors.length !== primaryFromDb.length ||
            secondarySectors.length !== secondaryFromDb.length ||
            tertiarySectors.length !== tertiaryFromDb.length ||
            primarySectors.map((ind) => ind.id).join() !==
                primaryFromDb.join() ||
            secondarySectors.map((ind) => ind.id).join() !==
                secondaryFromDb.join() ||
            tertiarySectors.map((ind) => ind.id).join() !==
                tertiaryFromDb.join()
        ) {
            return true
        } else {
            return false
        }
    }, [sectorPreferences, primarySectors, secondarySectors, tertiarySectors])

    useEffect(() => {
        setUnsavedChangesExist(unsavedChangesExist)
    }, [unsavedChangesExist, setUnsavedChangesExist])

    // reset form when we cancel out of editing
    useEffect(() => {
        if (!isEdit) resetForm()
    }, [isEdit, resetForm])

    const handleUpdate = async () => {
        try {
            const { data: mutationResult, errors } = await mutate({
                variables: {
                    candidateId: candidate.id,
                    preferences: [
                        ...primarySectors.map((sector) => ({
                            preferenceSectorId: sector.id,
                            order: 1,
                        })),
                        ...secondarySectors.map((sector) => ({
                            preferenceSectorId: sector.id,
                            order: 2,
                        })),
                        ...tertiarySectors.map((sector) => ({
                            preferenceSectorId: sector.id,
                            order: 3,
                        })),
                    ],
                },
            })
            if (mutationResult?.updateCandidateSectorPreferences.success) {
                await handleEditArray(sectionName, true)
                toast({
                    title: 'Success',
                    message: 'Sector preferences updated',
                    severity: 'success',
                })
                refetch()
                setUnsavedChangesExist(false)
            } else {
                console.log(errors)
                toast({
                    severity: 'error',
                    title: 'Error',
                    message: 'Failed to update sector preferences',
                })
            }
        } catch (error) {
            console.log(error)
            toast({
                severity: 'error',
                title: 'Error',
                message: 'Failed to update sector preferences',
            })
        }
    }

    return (
        <>
            <PreferenceSectionHeader
                handleEditArray={handleEditArray}
                sectionName={sectionName}
                title="Sector"
                subTitle="What sectors are you interested in working in?"
            />
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <ToggleEditMultiSelectField
                        edit={isEdit}
                        label="Primary Preference"
                        labelId="sector_primary_preference"
                        dropdownData={
                            SectorsOptions.data?.preferenceSectors.filter(
                                (sector) =>
                                    secondarySectors
                                        .concat(tertiarySectors)
                                        .map((sector) => sector.id)
                                        .indexOf(sector.id) === -1
                            ) || []
                        }
                        isDisabled={false}
                        onInputChange={(selectedOptions) =>
                            setPrimarySectors(selectedOptions)
                        }
                        selectedDropdownData={primarySectors}
                        limit={3}
                    />
                </Grid>
                <Grid item xs={6}>
                    <ToggleEditMultiSelectField
                        edit={isEdit}
                        label="Secondary Preference"
                        labelId="sector_secondary_preference"
                        isDisabled={false}
                        dropdownData={
                            SectorsOptions.data?.preferenceSectors.filter(
                                (sector) =>
                                    primarySectors
                                        .concat(tertiarySectors)
                                        .map((sector) => sector.id)
                                        .indexOf(sector.id) === -1
                            ) || []
                        }
                        selectedDropdownData={secondarySectors}
                        onInputChange={(selectedOptions) =>
                            setSecondarySectors(selectedOptions)
                        }
                        limit={3}
                    />
                </Grid>
                <Grid item xs={6}>
                    <ToggleEditMultiSelectField
                        edit={isEdit}
                        label="Tertiary Preference"
                        labelId="isector_tertiary_preference"
                        isDisabled={false}
                        dropdownData={
                            SectorsOptions.data?.preferenceSectors.filter(
                                (sector) =>
                                    secondarySectors
                                        .concat(primarySectors)
                                        .map((sector) => sector.id)
                                        .indexOf(sector.id) === -1
                            ) || []
                        }
                        selectedDropdownData={tertiarySectors}
                        onInputChange={(selectedOptions) =>
                            setTertiarySectors(selectedOptions)
                        }
                        limit={3}
                    />
                </Grid>
                {isEdit && (
                    <EditButtonContainer item xs={12}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                handleEditArray(sectionName, true)
                                setUnsavedChangesExist(false)
                                resetForm()
                            }}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleUpdate}>Save and Update</Button>
                    </EditButtonContainer>
                )}
            </Grid>
        </>
    )
}

export default Sector
